// 全局路由权限守卫：处理登录态校验、动态路由注入，以及教师端/后台的登录落地分流。
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";

import NProgress from "@/utils/nprogress";
import { TOKEN_KEY } from "@/enums/CacheEnum";
import router from "@/router";
import { routePermission } from "@/permission";
import {
  hasBackendRoleAccess,
  isOfficePath,
  redirectToExternalAdmin,
  resolveDefaultHomePath,
} from "@/permission/role-access";
import { usePermissionStore, useUserStore } from "@/store";

/**
 * 从登录页 query.redirect 中提取原始目标 path，用于判断这次登录是否来自教师端回跳。
 * @param route 当前 from 路由（通常是 /login）
 * @returns redirect 对应的 pathname；没有或非法时返回空字符串
 */
function resolveLoginRedirectPath(route: RouteLocationNormalized): string {
  const redirect = route.query.redirect;
  if (typeof redirect !== "string" || !redirect) return "";

  try {
    return new URL(decodeURIComponent(redirect), window.location.origin)
      .pathname;
  } catch {
    return "";
  }
}

/**
 * 登录后或访问通用首页时的落地路径：redirect 仅在有权限时保留，否则回角色默认首页。
 * @returns 需改道的 path；null 表示沿用当前 to
 */
function resolveLandingPath(
  roles: string[],
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): string | null {
  const defaultHome = resolveDefaultHomePath(roles);
  const fromLogin = from.path === "/login";
  const genericHome = to.path === "/" || to.path === "/dashboard";

  // / 与 /dashboard 统一落到当前角色可用的系统首页
  if (genericHome) {
    // 已在默认首页则不再改道，避免重复 next 导致导航失败（登录页 push 被 catch 吞掉）
    if (to.path === defaultHome) {
      return null;
    }
    return defaultHome;
  }

  // 有后台角色时默认优先进后台；但若登录页明确带着教师端 redirect，说明用户是从教师端被带来登录页，应尊重回跳。
  if (fromLogin && hasBackendRoleAccess(roles) && isOfficePath(to.path)) {
    if (isOfficePath(resolveLoginRedirectPath(from))) {
      return null;
    }
    return defaultHome;
  }

  // 登录页带 redirect：仅目标可访问时才保留，否则回默认首页（不进 401）
  if (fromLogin) {
    if (to.matched.length === 0) return defaultHome;
    if (hasPermissionKey(to) && !canAccessByPermission(to)) return defaultHome;
    return null;
  }

  return null;
}

/**
 * 管理员准备进入当前项目后台时，按开发环境开关切换到独立 Admin 项目。
 * @param roles 当前用户角色编码列表
 * @param path 本次准备访问的路由路径
 * @returns 是否已经发起独立后台跳转
 */
function redirectBackendRouteIfNeeded(roles: string[], path: string): boolean {
  if (!hasBackendRoleAccess(roles) || isOfficePath(path) || path === "/login") {
    return false;
  }
  return redirectToExternalAdmin(true);
}

/**
 * 权限初始化完成后，判断是否应改道至角色默认首页或合法 redirect 目标。
 * @returns true 表示已调用 next，调用方应 return
 */
function applyLandingOrDeny(
  roles: string[],
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  options: { replayTarget?: boolean } = {}
): boolean {
  const landingPath = resolveLandingPath(roles, to, from);
  if (landingPath) {
    next({ path: landingPath, replace: true });
    return true;
  }
  if (hasPermissionKey(to) && !canAccessByPermission(to)) {
    next("/401");
    return true;
  }
  if (options.replayTarget) {
    next({
      path: to.path,
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return true;
  }
  return false;
}

export function setupPermission() {
  // 白名单路由
  const whiteList = ["/login"];

  router.beforeEach(async (to, from, next) => {
    NProgress.start();
    const hasToken = localStorage.getItem(TOKEN_KEY);

    if (hasToken) {
      if (to.path === "/login") {
        const userStore = useUserStore();
        const roles = userStore.user.roles || [];
        if (roles.length) {
          routePermission.init(roles);
          if (redirectBackendRouteIfNeeded(roles, "/dashboard")) {
            NProgress.done();
            return;
          }
          next({ path: resolveDefaultHomePath(roles), replace: true });
        } else {
          next({ path: "/" });
        }
        NProgress.done();
      } else {
        const userStore = useUserStore();
        const hasRoles =
          userStore.user.roles && userStore.user.roles.length > 0;

        if (hasRoles) {
          routePermission.init(userStore.user.roles || []);
          if (
            redirectBackendRouteIfNeeded(userStore.user.roles || [], to.path)
          ) {
            NProgress.done();
            return;
          }
          if (applyLandingOrDeny(userStore.user.roles || [], to, from, next)) {
            NProgress.done();
            return;
          }
          // 如果未匹配到任何路由，跳转到404页面
          if (to.matched.length === 0) {
            console.warn(`[router] 未匹配路由: ${to.fullPath}`);
            next({
              path: resolveDefaultHomePath(userStore.user.roles || []),
              replace: true,
            });
            NProgress.done();
            return;
          } else if (hasPermissionKey(to) && !canAccessByPermission(to)) {
            next("/401");
          } else {
            // 如果路由参数中有 title，覆盖路由元信息中的 title
            const title =
              (to.params.title as string) || (to.query.title as string);
            if (title) {
              to.meta.title = title;
            }
            next();
          }
        } else {
          const permissionStore = usePermissionStore();
          try {
            await userStore.getUserInfo();
            routePermission.init(userStore.user.roles || []);
            if (
              redirectBackendRouteIfNeeded(userStore.user.roles || [], to.path)
            ) {
              NProgress.done();
              return;
            }
            const dynamicRoutes = await permissionStore.generateRoutes();
            dynamicRoutes.forEach((route: RouteRecordRaw) =>
              router.addRoute(route)
            );
            // 动态路由注册后重新解析目标，redirect 权限校验才准确
            const resolvedTo = router.resolve(
              to.fullPath
            ) as RouteLocationNormalized;
            if (
              applyLandingOrDeny(
                userStore.user.roles || [],
                resolvedTo,
                from,
                next,
                { replayTarget: true }
              )
            ) {
              NProgress.done();
              return;
            }
          } catch (error) {
            // 移除 token 并重定向到登录页，携带当前页面路由作为跳转参数
            await userStore.resetToken();
            redirectToLogin(to, next);
            NProgress.done();
          }
        }
      }
    } else {
      // 未登录
      if (whiteList.includes(to.path)) {
        next();
      } else {
        // 不在白名单，重定向到登录页
        redirectToLogin(to, next);
        NProgress.done();
      }
    }
  });

  router.afterEach(() => {
    NProgress.done();
  });
}

/** 重定向到登录页 */
function redirectToLogin(
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const params = new URLSearchParams(to.query as Record<string, string>);
  const queryString = params.toString();
  const redirect = queryString ? `${to.path}?${queryString}` : to.path;
  next(`/login?redirect=${encodeURIComponent(redirect)}`);
}

/** 判断是否有权限（按钮侧对齐老项目：走静态 permissionKey，不再读后端 perms） */
export function hasAuth(
  value: string | string[],
  type: "button" | "role" = "button"
) {
  const { roles } = useUserStore().user;

  if (type === "role") {
    return typeof value === "string"
      ? roles.includes(value)
      : value.some((role) => roles.includes(role));
  }

  // 超管直通（与 RoutePermissionManager 一致）
  if (roles.includes("ROOT") || roles.includes("admin")) {
    return true;
  }

  return typeof value === "string"
    ? routePermission.has(value)
    : routePermission.hasAny(value);
}

/** 路由是否配置了 permissionKey（无 key 则放行，兼容未标注模块） */
function hasPermissionKey(to: RouteLocationNormalized): boolean {
  if (to.meta?.permissionKey) {
    return true;
  }
  return to.matched.some((record) => Boolean(record.meta?.permissionKey));
}

/** 取最深一层带 permissionKey 的匹配记录做鉴权，避免父级模块 key 误伤子页 */
function canAccessByPermission(to: RouteLocationNormalized): boolean {
  for (let i = to.matched.length - 1; i >= 0; i -= 1) {
    const record = to.matched[i];
    if (record.meta?.permissionKey) {
      return routePermission.canAccess(to.path, record);
    }
  }
  return routePermission.canAccess(to.path, to);
}
